import { inject, Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, from, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

import { RestService } from '@hunting-nature/Secacam/shared/services/rest-service';
import { Store } from '@ngrx/store';
import { CameraModelService } from '@hunting-nature/shared-core/services/camera-model.service';
import { ToastService } from '@hunting-nature/Secacam/shared/services/toast.service';
import { IonNavService } from '@hunting-nature/Secacam/shared/services/ion-nav.service';
import { GeneralSelectors } from '@hunting-nature/Secacam/shared/redux/general/general.reducer';
import { NotificationSettingsActions } from './notification-settings.action';
import { CamerasPageService } from '@hunting-nature/Secacam/pages/cameras/services/cameras.page.service';
import { NotificationSettingsSelectors } from './notification-settings.reducer';

@Injectable()
export class NotificationSettingsEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly restService = inject(RestService);
  private readonly cameraService = inject(CamerasPageService);
  private readonly cameraModelService = inject(CameraModelService);
  private readonly toast = inject(ToastService);
  private readonly navController = inject(NavController);
  private readonly ionNavService = inject(IonNavService);

  notificationSettingsPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.notificationSettingsPageEntered),
      switchMap(() =>
        forkJoin({
          settings: this.restService.fetchGlobalNotificationSettings$(),
          cameras: this.cameraService.getCameras$().pipe(take(1)),
          models: this.cameraModelService.fetchCameraModels$(),
        }).pipe(
          map(({ settings, cameras, models }) =>
            NotificationSettingsActions.notificationSettingsPageEnteredSucceeded({
              settings,
              cameras: [...cameras.ownCameras, ...cameras.sharedCameras, ...cameras.servicePlanCameras],
              models,
            })
          ),
          catchError((error: unknown) => of(NotificationSettingsActions.notificationSettingsPageEnteredFailed({ error })))
        )
      )
    )
  );

  enableGlobalNotificationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.enableGlobalNotificationSettings),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.globalNotificationSettings)),
      switchMap(([action, globalNotificationSettings]) =>
        this.restService.updateGlobalNotificationSettings$(globalNotificationSettings!).pipe(
          map(() => NotificationSettingsActions.enableGlobalNotificationSettingsSucceeded({ settings: globalNotificationSettings! })),
          catchError((error: unknown) => of(NotificationSettingsActions.enableGlobalNotificationSettingsFailed({ error })))
        )
      )
    )
  );

  disableGlobalNotificationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.disableGlobalNotificationSettings),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.globalNotificationSettings)),
      switchMap(([action, globalNotificationSettings]) =>
        this.restService.updateGlobalNotificationSettings$(globalNotificationSettings!).pipe(
          map(() => NotificationSettingsActions.disableGlobalNotificationSettingsSucceeded()),
          catchError((error: unknown) => of(NotificationSettingsActions.disableGlobalNotificationSettingsFailed({ error })))
        )
      )
    )
  );

  setSecacamSoundAsNotificationSound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.setSecacamSoundAsNotificationSound),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.globalNotificationSettings)),
      switchMap(([action, globalNotificationSettings]) =>
        this.restService.updateGlobalNotificationSettings$(globalNotificationSettings!).pipe(
          map(() => NotificationSettingsActions.setSecacamSoundAsNotificationSoundSucceeded()),
          catchError((error: unknown) => of(NotificationSettingsActions.setSecacamSoundAsNotificationSoundFailed({ error })))
        )
      )
    )
  );

  setSystemSoundAsNotificationSound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.setSystemSoundAsNotificationSound),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.globalNotificationSettings)),
      switchMap(([action, globalNotificationSettings]) =>
        this.restService.updateGlobalNotificationSettings$(globalNotificationSettings!).pipe(
          map(() => NotificationSettingsActions.setSystemSoundAsNotificationSoundSucceeded()),
          catchError((error: unknown) => of(NotificationSettingsActions.setSystemSoundAsNotificationSoundFailed({ error })))
        )
      )
    )
  );

  addPushNotificationType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.addPushNotificationType),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.globalNotificationSettings)),
      switchMap(([action, globalNotificationSettings]) =>
        this.restService.updateGlobalNotificationSettings$(globalNotificationSettings!).pipe(
          map(() => NotificationSettingsActions.addPushNotificationTypeSucceeded()),
          catchError((error: unknown) => of(NotificationSettingsActions.addPushNotificationTypeFailed({ error })))
        )
      )
    )
  );

  removePushNotificationType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.removePushNotificationType),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.globalNotificationSettings)),
      switchMap(([action, globalNotificationSettings]) =>
        this.restService.updateGlobalNotificationSettings$(globalNotificationSettings!).pipe(
          map(() => NotificationSettingsActions.removePushNotificationTypeSucceeded()),
          catchError((error: unknown) => of(NotificationSettingsActions.removePushNotificationTypeFailed({ error })))
        )
      )
    )
  );

  addEmailNotificationType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.addEmailNotificationType),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.globalNotificationSettings)),
      switchMap(([action, globalNotificationSettings]) =>
        this.restService.updateGlobalNotificationSettings$(globalNotificationSettings!).pipe(
          map(() => NotificationSettingsActions.addEmailNotificationTypeSucceeded()),
          catchError((error: unknown) => of(NotificationSettingsActions.addEmailNotificationTypeFailed({ error })))
        )
      )
    )
  );

  removeEmailNotificationType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.removeEmailNotificationType),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.globalNotificationSettings)),
      switchMap(([action, globalNotificationSettings]) =>
        this.restService.updateGlobalNotificationSettings$(globalNotificationSettings!).pipe(
          map(() => NotificationSettingsActions.removeEmailNotificationTypeSucceeded()),
          catchError((error: unknown) => of(NotificationSettingsActions.removeEmailNotificationTypeFailed({ error })))
        )
      )
    )
  );

  notificationSettingsSelectCameraPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.notificationSettingsSelectCameraPageEntered),
      switchMap(() =>
        forkJoin({
          settings: this.restService.fetchGlobalNotificationSettings$(),
          cameras: this.cameraService.getCameras$().pipe(take(1)),
          models: this.cameraModelService.fetchCameraModels$(),
        }).pipe(
          map(({ settings, cameras, models }) =>
            NotificationSettingsActions.notificationSettingsSelectCameraPageEnteredSucceeded({
              settings,
              cameras: [...cameras.ownCameras, ...cameras.sharedCameras, ...cameras.servicePlanCameras],
              models,
            })
          ),
          catchError((error: unknown) => of(NotificationSettingsActions.notificationSettingsSelectCameraPageEnteredFailed({ error })))
        )
      )
    )
  );

  customNotificationSettingsPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.customNotificationSettingsPageEntered),
      switchMap(({ cameraId }) =>
        forkJoin({
          settings: this.restService.fetchCameraNotificationSettings$(cameraId),
          cameras: this.cameraService.getCameras$().pipe(take(1)),
          models: this.cameraModelService.fetchCameraModels$(),
        }).pipe(
          map(({ settings, cameras, models }) =>
            NotificationSettingsActions.customNotificationSettingsPageEnteredSucceeded({
              settings,
              cameras: [...cameras.ownCameras, ...cameras.sharedCameras, ...cameras.servicePlanCameras],
              models,
            })
          ),
          catchError((error: unknown) => of(NotificationSettingsActions.customNotificationSettingsPageEnteredFailed({ error })))
        )
      )
    )
  );

  saveCustomNotificationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationSettingsActions.saveCustomNotificationSettings),
      withLatestFrom(this.store.select(NotificationSettingsSelectors.customCameraNotificationSettings)),
      switchMap(([action, customCameraNotificationSettings]) =>
        this.restService.updateCameraNotificationSettings$(action.cameraId, customCameraNotificationSettings!).pipe(
          map(settings => NotificationSettingsActions.saveCustomNotificationSettingsSucceeded({ settings })),
          catchError((error: unknown) => of(NotificationSettingsActions.saveCustomNotificationSettingsFailed({ error })))
        )
      )
    )
  );

  saveCustomNotificationSettingsSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.saveCustomNotificationSettingsSucceeded),
        withLatestFrom(this.store.select(GeneralSelectors.isMenuOpen), this.store.select(NotificationSettingsSelectors.customCameraId)),
        switchMap(([, isMenuOpen, cameraId]) => (isMenuOpen ? from(this.ionNavService.goBack()) : this.navController.navigateBack(['home/cameras', cameraId!])))
      ),
    { dispatch: false }
  );

  changeNotificationSettingFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          NotificationSettingsActions.enableGlobalNotificationSettingsFailed,
          NotificationSettingsActions.disableGlobalNotificationSettingsFailed,
          NotificationSettingsActions.setSecacamSoundAsNotificationSoundFailed,
          NotificationSettingsActions.setSystemSoundAsNotificationSoundFailed,
          NotificationSettingsActions.addPushNotificationTypeFailed,
          NotificationSettingsActions.removePushNotificationTypeFailed,
          NotificationSettingsActions.addEmailNotificationTypeFailed,
          NotificationSettingsActions.removeEmailNotificationTypeFailed,
          NotificationSettingsActions.notificationSettingsPageEnteredFailed,
          NotificationSettingsActions.notificationSettingsSelectCameraPageEnteredFailed,
          NotificationSettingsActions.customNotificationSettingsPageEnteredFailed,
          NotificationSettingsActions.saveCustomNotificationSettingsFailed
        ),
        tap(() => this.toast.showTranslatedError$('menu.notificationSettings.error'))
      ),
    { dispatch: false }
  );

  discardCustomNotificationSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.discardCustomNotificationSettings),
        withLatestFrom(this.store.select(GeneralSelectors.isMenuOpen)),
        switchMap(([action, isMenuOpen]) =>
          isMenuOpen ? from(this.ionNavService.goBack()) : this.navController.navigateBack(['home/cameras', action.cameraId])
        )
      ),
    { dispatch: false }
  );

  // go tos
  goToMenuNotificationSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goToMenuNotificationSettings),
        switchMap(async () => {
          const NotificationSettingsPage = await import('../pages/global-notification-settings/global-notification-settings.page').then(
            m => m.GlobalNotificationSettingsPage
          );
          return from(this.ionNavService.pushPage(NotificationSettingsPage));
        })
      ),
    { dispatch: false }
  );

  goBackToMenuFromNotificationSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goBackToMenuFromNotificationSettings),
        switchMap(() => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );

  goToMenuNotificationSettingsSelectCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goToMenuNotificationSettingsSelectCamera),
        switchMap(async () => {
          const NotificationSettingsSelectCameraPage =
            await import('../pages/notification-settings-camera-selection/notification-settings-camera-selection.page').then(
              m => m.NotificationSettingsCameraSelectionPage
            );
          return from(this.ionNavService.pushPage(NotificationSettingsSelectCameraPage));
        })
      ),
    { dispatch: false }
  );

  goBackToMenuFromNotificationSettingsSelectCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goBackToMenuFromNotificationSettingsSelectCamera),
        switchMap(() => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );

  goToMenuCustomNotificationSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goToMenuCustomNotificationSettings),
        switchMap(async () => {
          const CustomNotificationSettingsPage = await import('../pages/custom-notification-settings/custom-notification-settings.page').then(
            m => m.CustomNotificationSettingsPage
          );
          return from(this.ionNavService.pushPage(CustomNotificationSettingsPage));
        })
      ),
    { dispatch: false }
  );

  goBackToMenuFromCustomNotificationSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goBackToMenuFromCustomNotificationSettings),
        switchMap(() => from(this.ionNavService.goBack()))
      ),
    { dispatch: false }
  );

  goToNotificationSettingsPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goToNotificationSettingsPage),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, 'notification-settings']))
      ),
    { dispatch: false }
  );

  goBackFromNotificationSettingsPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goBackFromNotificationSettingsPage),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId, 'custom-notification-settings']))
      ),
    { dispatch: false }
  );

  goToNotificationSettingsSelectCameraPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goToNotificationSettingsSelectCameraPage),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, 'notification-settings/select-camera']))
      ),
    { dispatch: false }
  );

  goBackFromNotificationSettingsSelectCameraPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goBackFromNotificationSettingsSelectCameraPage),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId, 'notification-settings']))
      ),
    { dispatch: false }
  );

  goToCustomNotificationSettingsPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goToCustomNotificationSettingsPage),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, 'custom-notification-settings']))
      ),
    { dispatch: false }
  );

  goBackFromCustomNotificationSettingsPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationSettingsActions.goBackFromCustomNotificationSettingsPage),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId]))
      ),
    { dispatch: false }
  );
}
